import { useId, useMemo, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, SearchInput, Skeleton } from '@aimsos/ui';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { iconForRecordType } from '../utils/recordTypeIcons.js';
import RecordTypeRailIconItem from './RecordTypeRailIconItem.jsx';
import RecordTypeRailItem from './RecordTypeRailItem.jsx';

export const ALL_RECORD_TYPES_ID = 'all';

const SKELETON_ROWS = 4;

/**
 * Sum of the type counts, or `null` when it cannot be known: while no type has
 * loaded yet or when any count is missing. A total that silently skips an
 * unknown count would read as a fact.
 */
const totalOf = (types) =>
  types.length > 0 && types.every((type) => typeof type.count === 'number')
    ? types.reduce((sum, type) => sum + type.count, 0)
    : null;

/**
 * Left panel of the Records list: "All" plus one option per record type, each
 * with its count. It is a `listbox` of `option` rows — a set of mutually
 * exclusive filters, not a menu — built from the design system's menu item.
 *
 * Kept local to this domain: it has a single consumer and no design-system
 * component of its own (`EntityHeader` is kept local for the same reason).
 *
 * @param {object} props
 * @param {Array<{ id: string, label: string, count: number|null }>} props.types
 *   What the endpoint returned, in its order. The rail adds "All" in front.
 * @param {string} props.selectedId `'all'` or a type id.
 * @param {(id: string) => void} props.onSelect Called with `'all'` or a type id.
 * @param {boolean} [props.loading=false] Shows placeholders instead of options.
 * @param {boolean} [props.collapsed=false] Icons only, no title and no search.
 * @param {() => void} [props.onToggleCollapsed] Without it the panel has no toggle.
 * @param {string} [props.className] Extra classes for the panel root.
 */
function RecordTypeRail({
  types,
  selectedId,
  onSelect,
  loading = false,
  collapsed = false,
  onToggleCollapsed,
  className = '',
}) {
  const { t } = useTranslation('unified-profiles');
  const titleId = useId();
  const [filter, setFilter] = useState('');

  const options = useMemo(
    () => [
      { id: ALL_RECORD_TYPES_ID, label: t('list.types.all'), count: totalOf(types) },
      ...types,
    ],
    [types, t],
  );

  // "All" is the way back out of any narrowing, so it never filters away.
  const visibleOptions = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return needle
      ? options.filter(
          (option) =>
            option.id === ALL_RECORD_TYPES_ID || option.label.toLowerCase().includes(needle),
        )
      : options;
  }, [options, filter]);

  const countLabel = (option) =>
    typeof option.count === 'number'
      ? `${option.label} · ${t('list.types.recordCount', { count: option.count })}`
      : option.label;

  const toggle = onToggleCollapsed ? (
    <Button
      variant="ghost"
      size="sm"
      leftIcon={collapsed ? PanelLeftOpen : PanelLeftClose}
      aria-label={collapsed ? t('list.types.expand') : t('list.types.collapse')}
      onClick={onToggleCollapsed}
    />
  ) : null;

  return (
    <section aria-labelledby={titleId} className={['flex min-w-0 flex-col gap-2 lg:border-r lg:border-edge lg:pr-4', className].filter(Boolean).join(' ')}>
      {collapsed ? (
        <div className="hidden lg:block">{toggle}</div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <h2
            id={titleId}
            className="px-2 text-xs font-semibold uppercase tracking-wide text-content-muted"
          >
            {t('list.types.title')}
          </h2>
          <div className="hidden lg:block">{toggle}</div>
        </div>
      )}
      {collapsed ? null : (
        <SearchInput
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder={t('list.types.filterPlaceholder')}
          aria-label={t('list.types.filterLabel')}
          disabled={loading}
        />
      )}
      <div
        role="listbox"
        aria-label={t('list.types.ariaLabel')}
        aria-busy={loading || undefined}
        className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible"
      >
        {loading ? (
          <div
            aria-hidden="true"
            data-testid="record-type-rail-skeleton"
            className="flex w-full gap-1 lg:flex-col"
          >
            {Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <Skeleton key={index} height="52px" width="100%" borderRadius="8px" />
            ))}
          </div>
        ) : (
          visibleOptions.map((option) =>
            collapsed ? (
              <RecordTypeRailIconItem
                key={option.id}
                id={option.id}
                name={countLabel(option)}
                icon={iconForRecordType(option.id)}
                selected={option.id === selectedId}
                onSelect={onSelect}
              />
            ) : (
            <RecordTypeRailItem
              key={option.id}
              id={option.id}
              label={option.label}
              count={option.count}
              icon={iconForRecordType(option.id)}
              selected={option.id === selectedId}
              onSelect={onSelect}
            />
            ),
          )
        )}
      </div>
      {!loading && !collapsed && visibleOptions.length === 1 && options.length > 1 ? (
        <p className="px-2 text-sm text-content-muted">{t('list.types.noMatch')}</p>
      ) : null}
    </section>
  );
}

export default RecordTypeRail;
